import {
  AttributeValue,
  DynamoDBClient,
  GetItemCommand,
  PutItemCommand,
  QueryCommand,
  UpdateItemCommand,
} from '@aws-sdk/client-dynamodb'
import { marshall, unmarshall } from '@aws-sdk/util-dynamodb'
import crypto from 'crypto'
import { calendlyEventSchema, CreateCalendlyEvent } from './schema'

const SALESFORCE_RECONCILIATION_LEASE_MS = 15 * 60 * 1000
const CREATE_AND_CONVERT_LEASE_MS = 15 * 60 * 1000

export class CalendlyEventRepository {
  private client: DynamoDBClient
  private tableName = 'calendly_events'

  constructor() {
    this.client = new DynamoDBClient({})
  }

  async create(event: CreateCalendlyEvent) {
    const id = crypto.randomUUID()

    const command = new PutItemCommand({
      TableName: this.tableName,
      Item: marshall({
        id,
        event,
        email: event.payload.email,
      }),
    })

    await this.client.send(command)

    return { id }
  }

  async getById(id: string) {
    const row = await this.getCalendlyEventDeliveryContext(id)
    return row === null ? null : calendlyEventSchema.parse(row.webhook)
  }

  async getCalendlyEventDeliveryContext(id: string): Promise<{
    webhook: unknown
    deliveryState: 'none' | 'in_flight' | 'delivered'
  } | null> {
    const command = new GetItemCommand({
      TableName: this.tableName,
      Key: marshall({ id }),
    })

    const result = await this.client.send(command)

    if (!result.Item) {
      return null
    }

    const item = unmarshall(result.Item as Record<string, AttributeValue>)
    const webhook = item.event

    if (webhook === undefined) {
      return null
    }

    const deliveryState = item.pcmEncoreDeliveredAt
      ? 'delivered'
      : item.pcmEncoreDeliveryInFlightAt
        ? 'in_flight'
        : 'none'

    return { webhook, deliveryState }
  }

  async tryBeginPcmEncoreDelivery(id: string): Promise<boolean> {
    try {
      await this.client.send(
        new UpdateItemCommand({
          TableName: this.tableName,
          Key: marshall({ id }),
          UpdateExpression: 'SET pcmEncoreDeliveryInFlightAt = :inFlightAt',
          ConditionExpression:
            'attribute_not_exists(pcmEncoreDeliveredAt) AND attribute_not_exists(pcmEncoreDeliveryInFlightAt)',
          ExpressionAttributeValues: marshall({ ':inFlightAt': new Date().toISOString() }),
        })
      )
      return true
    } catch (error) {
      if (error instanceof Error && error.name === 'ConditionalCheckFailedException') {
        return false
      }
      throw error
    }
  }

  async completePcmEncoreDelivery(id: string): Promise<void> {
    await this.client.send(
      new UpdateItemCommand({
        TableName: this.tableName,
        Key: marshall({ id }),
        UpdateExpression: 'SET pcmEncoreDeliveredAt = :deliveredAt REMOVE pcmEncoreDeliveryInFlightAt',
        ExpressionAttributeValues: marshall({ ':deliveredAt': new Date().toISOString() }),
      })
    )
  }

  async abortPcmEncoreDelivery(id: string): Promise<void> {
    await this.client.send(
      new UpdateItemCommand({
        TableName: this.tableName,
        Key: marshall({ id }),
        UpdateExpression: 'REMOVE pcmEncoreDeliveryInFlightAt',
      })
    )
  }

  async hasCreateAndConvertProcessed(id: string): Promise<boolean> {
    const command = new GetItemCommand({
      TableName: this.tableName,
      Key: marshall({ id }),
      ProjectionExpression: 'createAndConvertProcessedAt',
      ConsistentRead: true,
    })

    const result = await this.client.send(command)
    if (!result.Item) {
      return false
    }

    const item = unmarshall(result.Item as Record<string, AttributeValue>)
    return item.createAndConvertProcessedAt !== undefined
  }

  async tryBeginCreateAndConvertProcessing(id: string): Promise<boolean> {
    try {
      const now = new Date()
      const staleBefore = new Date(now.getTime() - CREATE_AND_CONVERT_LEASE_MS).toISOString()
      await this.client.send(
        new UpdateItemCommand({
          TableName: this.tableName,
          Key: marshall({ id }),
          UpdateExpression: 'SET createAndConvertInFlightAt = :inFlightAt',
          ConditionExpression:
            'attribute_exists(event) AND attribute_not_exists(createAndConvertProcessedAt) AND (attribute_not_exists(createAndConvertInFlightAt) OR createAndConvertInFlightAt < :staleBefore)',
          ExpressionAttributeValues: marshall({
            ':inFlightAt': now.toISOString(),
            ':staleBefore': staleBefore,
          }),
        })
      )
      return true
    } catch (error) {
      if (error instanceof Error && error.name === 'ConditionalCheckFailedException') {
        return false
      }
      throw error
    }
  }

  async completeCreateAndConvertProcessing(id: string, leadId?: string): Promise<void> {
    const now = new Date().toISOString()
    await this.client.send(
      new UpdateItemCommand({
        TableName: this.tableName,
        Key: marshall({ id }),
        UpdateExpression: leadId
          ? 'SET createAndConvertProcessedAt = :processedAt, createAndConvertLeadId = :leadId REMOVE createAndConvertInFlightAt'
          : 'SET createAndConvertProcessedAt = :processedAt REMOVE createAndConvertInFlightAt',
        ExpressionAttributeValues: marshall(
          leadId ? { ':processedAt': now, ':leadId': leadId } : { ':processedAt': now }
        ),
      })
    )
  }

  async abortCreateAndConvertProcessing(id: string): Promise<void> {
    await this.client.send(
      new UpdateItemCommand({
        TableName: this.tableName,
        Key: marshall({ id }),
        UpdateExpression: 'REMOVE createAndConvertInFlightAt',
      })
    )
  }

  async markSalesforceReconciliationPending(id: string): Promise<void> {
    try {
      await this.client.send(
        new UpdateItemCommand({
          TableName: this.tableName,
          Key: marshall({ id }),
          UpdateExpression: 'SET salesforceReconciliationPendingAt = :pendingAt',
          ConditionExpression:
            'attribute_exists(event) AND attribute_not_exists(salesforceReconciliationPendingAt) AND attribute_not_exists(salesforceReconciledAt)',
          ExpressionAttributeValues: marshall({ ':pendingAt': new Date().toISOString() }),
        })
      )
    } catch (error) {
      if (error instanceof Error && error.name === 'ConditionalCheckFailedException') {
        return
      }
      throw error
    }
  }

  async tryBeginSalesforceReconciliation(id: string): Promise<boolean> {
    try {
      const now = new Date()
      const staleBefore = new Date(now.getTime() - SALESFORCE_RECONCILIATION_LEASE_MS).toISOString()
      await this.client.send(
        new UpdateItemCommand({
          TableName: this.tableName,
          Key: marshall({ id }),
          UpdateExpression: 'SET salesforceReconciliationInFlightAt = :inFlightAt',
          ConditionExpression:
            'attribute_exists(salesforceReconciliationPendingAt) AND attribute_not_exists(salesforceReconciledAt) AND (attribute_not_exists(salesforceReconciliationInFlightAt) OR salesforceReconciliationInFlightAt < :staleBefore)',
          ExpressionAttributeValues: marshall({
            ':inFlightAt': now.toISOString(),
            ':staleBefore': staleBefore,
          }),
        })
      )
      return true
    } catch (error) {
      if (error instanceof Error && error.name === 'ConditionalCheckFailedException') {
        return false
      }
      throw error
    }
  }

  async completeSalesforceReconciliation(id: string): Promise<void> {
    await this.client.send(
      new UpdateItemCommand({
        TableName: this.tableName,
        Key: marshall({ id }),
        UpdateExpression: 'SET salesforceReconciledAt = :reconciledAt REMOVE salesforceReconciliationInFlightAt',
        ExpressionAttributeValues: marshall({ ':reconciledAt': new Date().toISOString() }),
      })
    )
  }

  async abortSalesforceReconciliation(id: string): Promise<void> {
    await this.client.send(
      new UpdateItemCommand({
        TableName: this.tableName,
        Key: marshall({ id }),
        UpdateExpression: 'REMOVE salesforceReconciliationInFlightAt',
      })
    )
  }

  async queryPendingSalesforceReconciliationsByEmail(email: string): Promise<{ id: string; webhook: unknown }[]> {
    const result = await this.client.send(
      new QueryCommand({
        TableName: this.tableName,
        IndexName: 'EmailIndex',
        KeyConditionExpression: 'email = :email',
        FilterExpression:
          'attribute_exists(salesforceReconciliationPendingAt) AND attribute_not_exists(salesforceReconciledAt)',
        ExpressionAttributeValues: marshall({ ':email': email }),
      })
    )

    if (!result.Items || result.Items.length === 0) {
      return []
    }

    return result.Items.flatMap((rawItem) => {
      const item = unmarshall(rawItem as Record<string, AttributeValue>)
      if (item.event === undefined || typeof item.id !== 'string') {
        return []
      }
      return [{ id: item.id, webhook: item.event }]
    })
  }
}
